/**
 * `/auth/login` — sign-in surface.
 *
 * Per the hi-fi mockup at docs/ui/wireframes/01-login.html and KEN_LAYER_7
 * §8.1. The form POSTs to /api/shell/auth/login; the backend mints either
 * a tenant-bound JWT (single-tenant user) or a pre-tenant JWT (multi-
 * tenant user) and sets it as an HttpOnly Secure SameSite=Strict cookie.
 *
 * Routing on response:
 *   200 + requires_tenant_select: false -> /<tenantSlug>/dashboard
 *      (the session refresh in AuthProvider picks up the tenant id and the
 *      cockpit redirects to the dashboard for that tenant)
 *   200 + requires_tenant_select: true  -> /auth/tenant-select
 *   400 / 401 / 403                     -> inline error banner
 */

import { Button, Card, CardContent, CardHeader, CardTitle, Input, Label } from "@ken/ui";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useState, type FormEvent, type ReactElement } from "react";

interface LoginSearch {
  reason?: "expired";
  from?: string;
}

export const Route = createFileRoute("/auth/login")({
  component: LoginRoute,
  validateSearch: (search: Record<string, unknown>): LoginSearch => ({
    // exactOptionalPropertyTypes: only set each key when it has a real value;
    // never assign an explicit `undefined`.
    ...(search["reason"] === "expired" ? { reason: "expired" as const } : {}),
    ...(typeof search["from"] === "string" ? { from: search["from"] } : {}),
  }),
});

interface LoginResponse {
  user_id?: string;
  requires_tenant_select?: boolean;
  /** mfa_two_factor: set when the identity has TOTP enabled. No usable session
   *  is minted — only the short-lived mfa-pending cookie. */
  requires_mfa?: boolean;
}

// Only honour same-origin paths to prevent open-redirect via `?from=https://evil`.
function safeReturnPath(from: string | undefined): string {
  if (!from) return "/";
  if (!from.startsWith("/")) return "/";
  if (from.startsWith("//")) return "/";
  if (from.startsWith("/auth/")) return "/";
  return from;
}

/**
 * Where a field-role person lands after sign-in (D-100.4): their company's
 * Documents page. Null for everyone else, or when the session cannot be read
 * (the ordinary landing then applies).
 */
async function fieldLanding(): Promise<string | null> {
  try {
    const res = await fetch("/api/shell/auth/session", { credentials: "include" });
    if (!res.ok) return null;
    const body = (await res.json()) as {
      user?: { roles?: string[] };
      tenant?: { id?: string; enabled_modules?: string[] };
      permissions?: string[];
    };
    const roles = body.user?.roles ?? [];
    const tenant = body.tenant?.id;
    const fieldOnly = roles.length > 0 && roles.every((r) => r === "field");
    if (!fieldOnly || !tenant) return null;
    if (!(body.permissions ?? []).includes("document_intake.view_own")) return null;
    if (!(body.tenant?.enabled_modules ?? []).includes("document_intake")) return null;
    return `/${tenant}/documents`;
  } catch {
    return null;
  }
}

function LoginRoute(): ReactElement {
  const navigate = useNavigate();
  const search = Route.useSearch();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const onSubmit = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);

    try {
      const res = await fetch("/api/shell/auth/login", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }),
      });

      if (res.status === 401) {
        setError("Email or password is incorrect.");
        return;
      }
      if (res.status === 403) {
        setError("This account has no tenant memberships. Contact your admin.");
        return;
      }
      if (!res.ok) {
        setError(`Sign-in failed (status ${res.status}).`);
        return;
      }

      const body = (await res.json()) as LoginResponse;
      if (body.requires_mfa) {
        // Password verified, but the identity has TOTP enabled. Only an
        // mfa-pending cookie is set (no usable session); send the user to the
        // challenge screen, carrying the original return path.
        await navigate({
          to: "/auth/mfa-challenge",
          search: search.from ? { from: search.from } : {},
        });
        return;
      }
      if (body.requires_tenant_select) {
        await navigate({ to: "/auth/tenant-select" });
      } else {
        // Tenant-bound JWT was minted. The AuthProvider polls /session and
        // will populate the tenant id; meanwhile the cockpit lands on the
        // tenant-scoped dashboard which redirects via its own loader.
        // If the user was bounced here by an expired session, return them
        // to the page they were on instead of the root.
        //
        // D-100.4 (W8-04-A-T3): a person whose only role is the field role
        // (drivers, technicians) lands straight on Documents, with no step
        // in between — their only screen.
        const landing = search.from ? null : await fieldLanding();
        window.location.assign(landing ?? safeReturnPath(search.from));
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Network error.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="grid place-items-center min-h-screen p-4 bg-[var(--color-bg-base)]">
      <Card surface="solid" className="w-full max-w-sm" padding="lg">
        <CardHeader>
          <div className="flex items-center gap-2.5 mb-1">
            <div className="grid place-items-center w-8 h-8 rounded-[var(--radius-md)] bg-[var(--color-brand)] text-white font-bold font-[var(--font-hand)] text-[22px]">
              K
            </div>
            <div className="font-[var(--font-hand)] text-[24px] font-semibold">ken</div>
          </div>
          <CardTitle>Sign in to Ken</CardTitle>
        </CardHeader>
        <CardContent>
          {search.reason === "expired" && error === null && (
            <div
              role="status"
              data-testid="auth-login-expired-banner"
              className="mb-4 text-sm text-[var(--color-text-on-glass-strong)] px-3 py-2 rounded-[var(--radius-sm)] bg-[oklch(96%_0.04_60_/_0.6)] border border-[oklch(80%_0.08_60)]"
            >
              Your session ended. Please sign in again to continue.
            </div>
          )}
          <form
            data-testid="auth-login-form"
            className="flex flex-col gap-4"
            onSubmit={onSubmit}
          >
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="you@company.co"
                data-testid="auth-login-form-email-input"
                required
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={submitting}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                placeholder="••••••••"
                data-testid="auth-login-form-password-input"
                required
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={submitting}
              />
            </div>
            {error !== null && (
              <div
                role="alert"
                data-testid="auth-login-form-error"
                className="text-sm text-[var(--color-accent-red)] px-3 py-2 rounded-[var(--radius-sm)] bg-[oklch(99%_0.04_25_/_0.4)] border border-[var(--color-accent-red)]"
              >
                {error}
              </div>
            )}
            <Button
              type="submit"
              variant="primary"
              data-testid="auth-login-form-submit"
              className="w-full"
              disabled={submitting}
            >
              {submitting ? "Signing in…" : "Sign in"}
            </Button>
          </form>
          <div className="mt-4 text-center text-xs text-[var(--color-text-soft)]">
            No account? Contact your admin.
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
